import { Box } from '@web/components/atoms/Layout'
import { graphql } from '@web/graphql-queries/index'
import useGraphQLClient from '../../../libs/hooks/useGraphQLClient'
import { useMutation } from '@tanstack/react-query'
import { CreateOrderInput, CreateOrderMutation, PaymentMethodType } from '@web/graphql-queries/graphql'
import { BASE_PAYMENT_INTENT, getDonorPaysAmount } from './utils'
import { DonationFormStep, useDonationFormContext } from '@web/components/page-components/DonationForm'
import { usePaymentStore } from '../../../libs/hooks/usePaymentStore'
import { useShallow } from 'zustand/react/shallow'
import { usePageStore } from '../../../libs/hooks/usePageStore'
import { PayPalButtons, usePayPalScriptReducer, DISPATCH_ACTION, SCRIPT_LOADING_STATE } from '@paypal/react-paypal-js'
import { memo, useContext, useEffect, useMemo, useRef } from 'react'
import { DonationFormContext } from '@web/components/page-components/DonationForm/DonationFormProvider'
import { prefetchThankYouPageLayout } from 'apps/fe-donation/libs/queries'
import { v4 } from 'uuid'
import { getFacebookPixelCookies, generateOrGetDeviceUUID } from 'apps/fe-donation/utils/analytics'
import Decimal from 'decimal.js'

const createOrderDocument = graphql(/* GraphQL */ `
  mutation CreateOrder($input: CreateOrderInput!) {
    createOrder(input: $input) {
      id
      aggregateId
    }
  }
`)

type PayPalButtonProps = {
  onReady: () => void
}

type ButtonWrapperProps = {
  id: string
  onReady: () => void
  createOrder: () => Promise<string>
}
const ButtonWrapper = ({ id, onReady, createOrder }: ButtonWrapperProps) => {
  const [{ isResolved }, dispatch] = usePayPalScriptReducer()
  const { setStep, nextStep } = useDonationFormContext((state) => state.actions)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const rect = ref.current?.getBoundingClientRect()
    const isHidden = rect?.width === 0 && rect?.top === 0

    if (isHidden) {
      return
    }

    if (isResolved) {
      onReady()

      return
    }

    dispatch({ type: DISPATCH_ACTION.LOADING_STATUS, value: SCRIPT_LOADING_STATE.PENDING })
  }, [isResolved])

  return (
    <Box w="100%" minH={isResolved ? '50px' : 0} ref={ref} data-testid="donate-paypal">
      <PayPalButtons
        createOrder={createOrder}
        onApprove={async (_, __): Promise<void> => {
          nextStep()
          console.log('approved paypal step')
        }}
        onError={async (err): Promise<void> => {
          setStep(DonationFormStep.ErrorStep)
          console.error('error paypal step', err)
        }}
        onCancel={async (): Promise<void> => {
          console.log('cancelled paypal modal')
        }}
        fundingSource="paypal"
        style={{ height: 50 }}
      />
    </Box>
  )
}

const PayPalButton = memo(function PayPalButtonWrapper(props: PayPalButtonProps) {
  const buttonId = useMemo(() => v4(), [])
  const graphQLClient = useGraphQLClient()

  const { mutateAsync: createOrderMutation } = useMutation({
    mutationFn: (input: CreateOrderInput) =>
      graphQLClient<CreateOrderMutation>({
        document: createOrderDocument,
        options: {
          input,
        },
      }),
  })

  const donationFormStore = useContext(DonationFormContext)
  const { validateForm } = useDonationFormContext((state) => state.actions)
  const paypalSettings = usePaymentStore(
    (state) => state.paymentSettings?.donationPagePaymentProviderSettings?.payPalMerchantSetting,
  )
  const { merchantId } = paypalSettings || {}
  const hasErrors = useDonationFormContext(
    useShallow(({ errors }) => Object.keys(errors).length > 0 || !validateForm()),
  )

  if (!merchantId) {
    return null
  }

  const createOrder = async () => {
    const { charityId, campaignId, pageId, pageBucketId, isLiveMode, profileId, profileData } = usePageStore.getState()
    const formValues = donationFormStore!.getState().formValues
    const donorPaysAmount = getDonorPaysAmount(formValues.amount ?? 0)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .mul(100)
      .toNumber()
    usePaymentStore.getState().actions.setPaymentMethodType(PaymentMethodType.PayPal)
    const purchaseEventId = v4()
    usePageStore.setState({ purchaseEventId })
    const { fbp, fbc } = getFacebookPixelCookies()
    const deviceUUID = generateOrGetDeviceUUID()

    const { createOrder: res } = await createOrderMutation({
      ...BASE_PAYMENT_INTENT,
      charityId,
      campaignId,
      pageId: pageId!,
      pageBucketId,
      profileId,
      profileName: profileData?.name,
      donationFormAmount: formValues.amount ?? 0,
      email: formValues.extraFields?.email ?? '',
      cancelUrl: window.location.href,
      returnUrl: window.location.href,
      donationPageUrl: window.location.href,
      isDonorChooseToPayFee: formValues.isDonorPays,
      donorPaysAmount: formValues.isDonorPays ? donorPaysAmount : 0,
      isLiveMode,
      fbp,
      fbc,
      deviceUUID,
      eventId: purchaseEventId,
    })

    usePaymentStore.setState((state) => ({
      ...state,
      paymentTransactionId: res.aggregateId,
    }))

    if (!usePageStore.getState().thankYouPageLayout) {
      prefetchThankYouPageLayout()
    }

    return res.id ?? ''
  }

  return (
    <Box w="100%" onClick={() => validateForm(true)} cursor="pointer" zIndex={0}>
      <Box w="100%" pointerEvents={hasErrors ? 'none' : 'auto'}>
        <ButtonWrapper id={buttonId} onReady={props.onReady} createOrder={createOrder} />
      </Box>
    </Box>
  )
})

export default PayPalButton
